import React from "react";
import styled from "styled-components";
import Modal from "react-modal";

import Calc from "./Calc.jsx";
import { Gebhart, A, B, CTotal, D, TotalCalculatedAlbedo } from "./equations";
import { DateContext, DirectionContext, TimeContext } from "../App";
import { CustomButton, SectionContainer } from "./Custom";
import { roundToNDecimalPlaces } from "./utils.js";

Modal.setAppElement("#root");

const Container = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
`;

const MapContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  height: auto;
`;

const StyledGorge = styled.div`
  box-sizing: border-box;
  background-color: grey;
  height: ${(props) => `${props.nCells * 30}px`};
`;

const StyledCell = styled.div`
  box-sizing: border-box;
  border: 1px solid black;
  width: auto;
  height: 30px;
  background-color: ${(props) =>
    props.selected ? "rgba(150, 80, 99, 0.5)" : "#FFFFFF"};
`;

const StyledModal = styled(Modal)`
  box-sizing: border-box;
  max-width: 400px;
  margin: 10vh auto;
  background-color: white;
  border: 1px solid #a0a0a0;
  box-shadow: 0 0 16px 4px rgb(0 0 0 / 15%);
  border-radius: 8px;
`;

export const SingleSectionContainer = styled.div`
  box-sizing: border-box;
  align-items: center;
  margin: 1rem;
  text-align: left;
  max-width: 400px;
`;

const Gorge = ({ setCurCell, ...props }) => (
  <StyledGorge onClick={(e) => setCurCell("gorge")} {...props} />
);

const Cell = ({ id, curCell, setCurCell, selected }) => {
  return (
    <StyledCell
      selected={selected}
      onClick={(e) => setCurCell(id)}></StyledCell>
  );
};

const Tiles = ({ curCell, setCurCell, cellIds }) => {
  return (
    <div>
      {cellIds.map((id) => {
        return (
          <Cell
            key={id}
            id={id}
            curCell={curCell}
            setCurCell={setCurCell}
            selected={id === curCell}
          />
        );
      })}
    </div>
  );
};

const Map = ({
  cellProps,
  dispatchCellProps,
  multiplier,
  nOfCells,
  idSetLeft,
  idSetRight,
  streetWidth,
  cellWidth,
}) => {
  const [curCell, setCurCell] = React.useState(null);
  const direction = React.useContext(DirectionContext);
  const time = React.useContext(TimeContext);
  const date = React.useContext(DateContext);
  const [showDialog, setShowDialog] = React.useState(false);
  const [albedo, setAlbedo] = React.useState(0.0);

  const focusOnCell = (cellId) => {
    setCurCell(cellId);
    setShowDialog(true);
  };

  const closeDialog = () => setShowDialog(false);

  let g, a, b, c, d;
  let t = 0.0;

  const calcAlbedo = (e) => {
    e.preventDefault();

    if (cellProps) {
      g = Gebhart(
        cellProps,
        idSetLeft,
        idSetRight,
        nOfCells,
        cellWidth,
        streetWidth,
      );

      a = A(cellProps, time, date);

      b = B(cellProps, time, date);

      c = CTotal(g, cellProps, idSetLeft, idSetRight, time, date);

      d = D(cellProps, time, date);

      t = multiplier * TotalCalculatedAlbedo(a, b, c, d);

      setAlbedo(roundToNDecimalPlaces(t, 3));
    }
  };

  return (
    <Container>
      <SectionContainer>
        <div>
          Click on cells to set material composition and height. After setting
          properties for all the cells, click the "Calculate Albedo" button.
        </div>
        <CustomButton onClick={calcAlbedo}>Calculate Albedo</CustomButton>
      </SectionContainer>
      <SingleSectionContainer>
        Albedo: {albedo > 0 ? albedo : "N/A"}
      </SingleSectionContainer>
      <MapContainer>
        <Tiles curCell={curCell} setCurCell={focusOnCell} cellIds={idSetLeft} />
        <Gorge setCurCell={focusOnCell} nCells={nOfCells} />
        <Tiles
          curCell={curCell}
          setCurCell={focusOnCell}
          cellIds={idSetRight}
        />
      </MapContainer>
      {curCell && showDialog && (
        <StyledModal isOpen={showDialog}>
          <Calc
            id="cell-properties-modal"
            cellId={curCell}
            cellParams={cellProps}
            dispatchCellProps={dispatchCellProps}
            cellMats={
              (cellProps[curCell] && cellProps[curCell].materials) || {}
            }
            cellHeight={(cellProps[curCell] && cellProps[curCell].height) || 0}
            cellDir={idSetLeft.includes(curCell) ? direction[1] : direction[3]}
            closeDialog={closeDialog}
          />
        </StyledModal>
      )}
    </Container>
  );
};

export default Map;
